import { omit } from 'lodash';
import { v4 } from 'uuid';

import { DetailedPage, Page } from '@models/Page';
import { PageSectionSource, SOURCE_INSTANCE_ID } from '@models/PageSection';
import { ValidationError } from '@models/ValidationError';

import { createAdminApi, getApiBaseUrl } from '../shared/utils';

const LEGACY_TITLE_KEY = 'TITLE_KEY';

const getLegacyTitleKey = (source: PageSectionSource) => {
  const legacyKey = source.params?.[LEGACY_TITLE_KEY as keyof typeof source.params];
  return typeof legacyKey === 'string' ? legacyKey : undefined;
};

/**
 * Adds a frontend-only key to sources since there can be multiple instances of the same source in
 * each section, and sourceId thus can not be used as key.
 */
const pageDtoToPage = (page?: DetailedPage): DetailedPage | undefined =>
  page
    ? {
        ...page,
        sections: page.sections?.map(section => ({
          ...section,
          sources: section.sources?.map(source => ({
            ...source,
            [SOURCE_INSTANCE_ID]: source[SOURCE_INSTANCE_ID] ?? getLegacyTitleKey(source) ?? v4(),
            params: source.params ? omit(source.params, LEGACY_TITLE_KEY) : undefined,
          })),
        })),
      }
    : undefined;

/**
 * Removes frontend-only source keys.
 */
export const pageToPageDto = (page: DetailedPage): DetailedPage => ({
  ...page,
  sections: page.sections?.map(section => ({
    ...section,
    sources: section.sources?.map(source => {
      let tmpSource = omit(source, SOURCE_INSTANCE_ID) as PageSectionSource;
      try {
        if (tmpSource.params)
          tmpSource = { ...tmpSource, params: omit(tmpSource.params, LEGACY_TITLE_KEY) };
      } catch (e) {
        console.error(e);
      }
      return tmpSource;
    }),
  })),
});

const api = createAdminApi(`${getApiBaseUrl('layout')}/admin/pages`, { 'x-api-version': '1.0' });

export const fetchPage = (id: string) =>
  api
    .get<DetailedPage>(id)
    .then(response =>
      response.ok ? { ...response, data: pageDtoToPage(response.data) } : response
    );

export const fetchAllPages = () => api.get<Page[]>('');

export const fetchPagesReferencedBySourceId = (sourceId: string) =>
  api.get<Page[]>(`referencedBy/${sourceId}`);

export const savePage = (page: DetailedPage) =>
  (page.id ? api.put : api.post)<DetailedPage | ValidationError>(page.id, pageToPageDto(page));

export const deletePage = (pageId: string) => api.delete(pageId);
